<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header Section -->
      <div>
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              Project Reports
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              Manage and configure project reports
            </p>
          </div>

          <div class="flex items-center space-x-2">
            <RouterLink
              :to="{ name: 'CreateReport', params: { projectId } }"
              class="btn btn--primary-blue flex items-center space-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

              <span>Create Report</span>
            </RouterLink>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="fetchReportsAction.is(States.LOADING)"
        class="mt-8 flex justify-center"
      >
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="reports.length === 0"
        class="mt-8 text-center py-12 element-island flex flex-col items-center justify-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-400">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

        <h3 class="mt-2 text-sm font-medium text-gray-900">
          No reports configured
        </h3>

        <p class="mt-1 text-sm text-gray-500">
          Get started by creating your first project report.
        </p>

        <div class="mt-4">
          <RouterLink
            :to="{ name: 'CreateReport', params: { projectId } }"
            class="btn btn--primary-blue flex items-center space-x-2 mx-auto"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>Create Report</span>
          </RouterLink>
        </div>
      </div>

      <!-- Reports List -->
      <div
        v-else
        class="mt-8 space-y-4"
      >
        <div
          v-for="report in reports"
          :key="report.id"
          class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
        >
          <div class="p-6">
            <!-- Report Header -->
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center space-x-3">
                  <h3 class="text-lg font-semibold text-gray-900">
                    {{ report.title }}
                  </h3>

                  <!-- Status Badges -->
                  <div class="flex items-center space-x-2">
                    <span
                      v-if="report.document_control_version"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      Document Version Control
                    </span>

                    <span
                      v-if="report.save_export"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      Report Saved to Cloud
                    </span>
                  </div>
                </div>

                <p class="mt-1 text-sm text-gray-600">
                  Report file name: {{ report.export_file_name }}.pdf
                </p>
              </div>

              <div class="flex items-center space-x-2">
                <button
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="showGenerateModal(report)"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" class="w-4 h-4">
<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                  <span>Generate</span>
                </button>

                <RouterLink
                  v-if="report.save_export"
                  :to="{ name: 'ReportShow', params: { projectId, reportId: report.id } }"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
    <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>

                  <span>View Saved Reports</span>
                </RouterLink>

                <RouterLink
                  :to="{ name: 'ReportEditor', params: { projectId, reportId: report.id } }"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                >
                  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>

                  <span>Edit</span>
                </RouterLink>
              </div>
            </div>

            <!-- Report Details -->
            <div class="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Recipients -->
              <div class="flex items-start space-x-2">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
</svg>

                <div class="min-w-0 flex-1">
                  <p class="text-xs font-medium text-gray-500">
                    Recipients
                  </p>

                  <p class="text-sm text-gray-900">
                    {{ report.issue_to?.length || 0 }} {{ report.issue_to?.length === 1 ? 'person' : 'people' }}
                  </p>
                </div>
              </div>

              <!-- Report Sections -->
              <div class="flex items-start space-x-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

                <div class="min-w-0 flex-1">
                  <p class="text-xs font-medium text-gray-500">
                    Sections
                  </p>

                  <p class="text-sm text-gray-900">
                    {{ report.report_section_configs.length || 0 }} {{ report.report_section_configs.length === 1 ? 'section' : 'sections' }}
                  </p>
                </div>
              </div>

              <div class="flex items-start space-x-2">
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>

                <div class="min-w-0 flex-1">
                  <p class="text-xs font-medium text-gray-500">
                    Auto Generated
                  </p>

                  <p class="text-sm text-gray-900">
                    {{ report.auto_generate_day ? `Day ${report.auto_generate_day} of every month` : 'Disabled.' }}
                  </p>
                </div>
              </div>

              <div
                v-if="report.auto_generate_day"
                class="flex items-start space-x-2"
              >
                <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    stroke-width="2"
 class="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0">
    <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
    />

    <path
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M12 6v6l4 2"
    />
</svg>

                <div
                  v-if="report.auto_generate_day"
                  class="min-w-0 flex-1"
                >
                  <p class="text-xs font-medium text-gray-500">
                    Next Auto Generated Report
                  </p>

                  <p class="text-sm text-gray-900">
                    {{ DateTime.now().set({ day: report.auto_generate_day }).toLocaleString(DateTime.DATE_FULL) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Recipients List (if any) -->
            <div
              v-if="report.issue_to && report.issue_to.length > 0"
              class="mt-4 pt-4 border-t border-gray-200"
            >
              <p class="text-xs font-medium text-gray-500 mb-2">
                Recipients:
              </p>

              <div class="flex flex-wrap gap-2">
                <span
                  v-for="recipient in report.issue_to"
                  :key="recipient.email"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                >
                  <span v-if="recipient.user">
                    {{ recipient.user }}
                  </span>
                  {{ recipient.email }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <GenerateExportModal
      v-if="showExportReportModal"
      :report="selectedReport"
      @close="showExportReportModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import GenerateExportModal from '@/components/Reports/ReportShow/GenerateExportModal.vue';
import useBoqLockRedirect from '@/composables/useBoqLockRedirect';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import Report from '@/models/Report';
import { useReportsStore } from '@/store/reports';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const reportsStore = useReportsStore();
const fetchReportsAction = useStoreApiAction(reportsStore.index);
// Modal state management
const showExportReportModal = ref(false);
const selectedReport = ref<Report | undefined>();

// Fetch reports on component mount
watch(() => props.projectId, (newValue) => {
  if(newValue) {
    fetchReportsAction.request(newValue).catch(console.error);
  }
}, { immediate: true });

// Computed properties
const reports = computed(() => {
  return reportsStore.models.where('project_id', parseInt(props.projectId)).with('report_section_configs').get();
});

// Modal functions
const showGenerateModal = (report: Report) => {
  selectedReport.value = report;
  showExportReportModal.value = true;
};

useBoqLockRedirect();
</script>

<style scoped></style>
